// BidIQ — Document Export Utilities (PDF, Word, Excel)
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { Document, Packer, Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, WidthType, BorderStyle, AlignmentType } from "docx";
import * as XLSX from "xlsx";

// ── Helpers ──

function parseMarkdownTables(md) {
  // Extract markdown tables into arrays of {headers, rows}
  const tables = [];
  const lines = md.split("\n");
  let i = 0;
  while (i < lines.length) {
    const line = lines[i].trim();
    if (line.startsWith("|") && lines[i + 1] && lines[i + 1].trim().match(/^\|[\s-:|]+\|$/)) {
      const headers = line.split("|").filter(c => c.trim()).map(c => c.trim());
      i += 2; // skip header + separator
      const rows = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        rows.push(lines[i].trim().split("|").filter(c => c.trim()).map(c => c.trim()));
        i++;
      }
      tables.push({ headers, rows });
    } else {
      i++;
    }
  }
  return tables;
}

function stripMarkdown(text) {
  return text
    .replace(/#{1,6}\s/g, "")
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/\*(.*?)\*/g, "$1")
    .replace(/`(.*?)`/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");
}

function getTimestamp() {
  return new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

// ── PDF Export ──

export function exportPDF(content, title = "BidIQ Report") {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const maxWidth = pageWidth - margin * 2;
  let y = 20;

  // Header bar
  doc.setFillColor(0, 48, 87); // navy
  doc.rect(0, 0, pageWidth, 28, "F");
  doc.setFillColor(179, 163, 105); // gold accent
  doc.rect(0, 28, pageWidth, 2, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("C&S Chemicals", margin, 12);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("Bid Command Center", margin, 19);
  doc.setFontSize(9);
  doc.text(getTimestamp(), pageWidth - margin, 12, { align: "right" });

  y = 40;
  doc.setTextColor(30, 30, 30);

  // Parse content into sections
  const lines = content.split("\n");
  const tables = parseMarkdownTables(content);
  let tableIndex = 0;
  let inTable = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Check page break
    if (y > doc.internal.pageSize.getHeight() - 30) {
      doc.addPage();
      y = 20;
    }

    // Skip table rows (handled by autotable)
    if (line.startsWith("|")) {
      if (!inTable && tableIndex < tables.length) {
        inTable = true;
        const t = tables[tableIndex];
        doc.autoTable({
          startY: y,
          head: [t.headers],
          body: t.rows,
          margin: { left: margin, right: margin },
          styles: { fontSize: 8, cellPadding: 3 },
          headStyles: { fillColor: [0, 48, 87], textColor: 255, fontStyle: "bold" },
          alternateRowStyles: { fillColor: [245, 245, 245] },
        });
        y = doc.lastAutoTable.finalY + 10;
        tableIndex++;
      }
      continue;
    }
    if (inTable && !line.startsWith("|")) {
      inTable = false;
    }

    // Headers
    if (line.startsWith("### ")) {
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 48, 87);
      doc.text(stripMarkdown(line.replace("### ", "")), margin, y);
      y += 7;
    } else if (line.startsWith("## ")) {
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 48, 87);
      y += 4;
      doc.text(stripMarkdown(line.replace("## ", "")), margin, y);
      y += 8;
    } else if (line.startsWith("# ")) {
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 48, 87);
      y += 6;
      doc.text(stripMarkdown(line.replace("# ", "")), margin, y);
      y += 10;
    } else if (line.startsWith("- ") || line.startsWith("* ")) {
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(30, 30, 30);
      const bulletText = stripMarkdown(line.replace(/^[-*]\s/, ""));
      const splitLines = doc.splitTextToSize("\u2022  " + bulletText, maxWidth - 5);
      doc.text(splitLines, margin + 5, y);
      y += splitLines.length * 5;
    } else if (line === "") {
      y += 3;
    } else {
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(30, 30, 30);
      const splitLines = doc.splitTextToSize(stripMarkdown(line), maxWidth);
      doc.text(splitLines, margin, y);
      y += splitLines.length * 5;
    }
  }

  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFontSize(7);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Generated by BidIQ \u2022 C&S Bid Command Center \u2022 ${getTimestamp()} \u2022 Page ${p}/${pageCount}`,
      pageWidth / 2, doc.internal.pageSize.getHeight() - 8,
      { align: "center" }
    );
  }

  doc.save(`${title.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ── Word (.docx) Export ──

export async function exportWord(content, title = "BidIQ Report") {
  const sections = [];
  const lines = content.split("\n");
  const tables = parseMarkdownTables(content);
  let tableIndex = 0;
  let inTable = false;

  // Title
  sections.push(
    new Paragraph({
      children: [new TextRun({ text: "C&S Chemicals", bold: true, size: 32, color: "003057", font: "Calibri" })],
      spacing: { after: 40 },
    }),
    new Paragraph({
      children: [new TextRun({ text: `${title} \u2022 ${getTimestamp()}`, size: 18, color: "B3A369", font: "Calibri" })],
      spacing: { after: 300 },
      border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: "B3A369" } },
    })
  );

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    if (line.startsWith("|")) {
      if (!inTable && tableIndex < tables.length) {
        inTable = true;
        const t = tables[tableIndex];
        const headerRow = new TableRow({
          children: t.headers.map(h => new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: h, bold: true, size: 18, color: "FFFFFF", font: "Calibri" })] })],
            shading: { fill: "003057" },
            width: { size: Math.floor(9000 / t.headers.length), type: WidthType.DXA },
          })),
          tableHeader: true,
        });
        const bodyRows = t.rows.map((row, ri) => new TableRow({
          children: row.map(cell => new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: cell, size: 18, font: "Calibri" })] })],
            shading: ri % 2 === 1 ? { fill: "F5F5F5" } : {},
            width: { size: Math.floor(9000 / t.headers.length), type: WidthType.DXA },
          })),
        }));
        sections.push(new Table({ rows: [headerRow, ...bodyRows], width: { size: 9000, type: WidthType.DXA } }));
        sections.push(new Paragraph({ spacing: { after: 200 } }));
        tableIndex++;
      }
      continue;
    }
    if (inTable) inTable = false;

    if (line.startsWith("### ")) {
      sections.push(new Paragraph({
        children: [new TextRun({ text: stripMarkdown(line.replace("### ", "")), bold: true, size: 22, color: "003057", font: "Calibri" })],
        heading: HeadingLevel.HEADING_3,
        spacing: { before: 200, after: 80 },
      }));
    } else if (line.startsWith("## ")) {
      sections.push(new Paragraph({
        children: [new TextRun({ text: stripMarkdown(line.replace("## ", "")), bold: true, size: 26, color: "003057", font: "Calibri" })],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 280, after: 100 },
      }));
    } else if (line.startsWith("# ")) {
      sections.push(new Paragraph({
        children: [new TextRun({ text: stripMarkdown(line.replace("# ", "")), bold: true, size: 32, color: "003057", font: "Calibri" })],
        heading: HeadingLevel.HEADING_1,
        spacing: { before: 320, after: 120 },
      }));
    } else if (line.startsWith("- ") || line.startsWith("* ")) {
      sections.push(new Paragraph({
        children: [new TextRun({ text: stripMarkdown(line.replace(/^[-*]\s/, "")), size: 20, font: "Calibri" })],
        bullet: { level: 0 },
        spacing: { after: 40 },
      }));
    } else if (line === "") {
      sections.push(new Paragraph({ spacing: { after: 80 } }));
    } else {
      // Handle bold within paragraphs
      const parts = line.split(/(\*\*.*?\*\*)/);
      const runs = parts.map(part => {
        if (part.startsWith("**") && part.endsWith("**")) {
          return new TextRun({ text: part.slice(2, -2), bold: true, size: 20, font: "Calibri" });
        }
        return new TextRun({ text: stripMarkdown(part), size: 20, font: "Calibri" });
      });
      sections.push(new Paragraph({ children: runs, spacing: { after: 60 } }));
    }
  }

  // Footer
  sections.push(
    new Paragraph({ spacing: { before: 400 }, border: { top: { style: BorderStyle.SINGLE, size: 2, color: "CCCCCC" } } }),
    new Paragraph({
      children: [new TextRun({ text: `Generated by BidIQ \u2022 C&S Bid Command Center \u2022 ${getTimestamp()}`, size: 14, color: "999999", font: "Calibri", italics: true })],
      alignment: AlignmentType.CENTER,
    })
  );

  const doc = new Document({
    sections: [{ children: sections }],
  });

  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${title.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.docx`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Excel (.xlsx) Export ──

export function exportExcel(content, title = "BidIQ Report") {
  const tables = parseMarkdownTables(content);
  if (tables.length === 0) {
    // No tables found — export the full text as a single-column sheet
    const lines = content.split("\n").filter(l => l.trim());
    const ws = XLSX.utils.aoa_to_sheet([["BidIQ Report"], [getTimestamp()], [], ...lines.map(l => [stripMarkdown(l.trim())])]);
    ws["!cols"] = [{ wch: 80 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, `${title.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    return;
  }

  const wb = XLSX.utils.book_new();
  tables.forEach((t, idx) => {
    const data = [t.headers, ...t.rows];
    const ws = XLSX.utils.aoa_to_sheet(data);
    // Auto-size columns
    ws["!cols"] = t.headers.map((_, ci) => {
      const maxLen = Math.max(t.headers[ci].length, ...t.rows.map(r => (r[ci] || "").length));
      return { wch: Math.min(maxLen + 4, 40) };
    });
    XLSX.utils.book_append_sheet(wb, ws, `Table ${idx + 1}`);
  });

  XLSX.writeFile(wb, `${title.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

// ── Copy to Clipboard ──

export async function copyToClipboard(content) {
  try {
    await navigator.clipboard.writeText(content);
    return true;
  } catch {
    // Fallback
    const ta = document.createElement("textarea");
    ta.value = content;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    return true;
  }
}
